/**
 * Index page for the "Ledgers" section.
 *
 * Layout:
 *   PageToolbar — title + search + "Show download buttons" toggle
 *   KPI summary — 3 cards (net position, upline debt, client receivables)
 *   List       — desktop table; mobile cards below the `md:` breakpoint
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  FileText,
  ImageIcon,
  Users,
} from 'lucide-react';
import { api } from '../api';
import type { OpeningBalance, Party, PartyBalance } from '../api';
import { AppShell } from '../ui/AppShell';
import { PageToolbar } from '../ui/PageToolbar';
import { Btn, Card, EmptyState, RowSkeleton, StatePill } from '../ui/components';
import { Checkbox, TextInput } from '../ui/primitives';

const SHOW_DL_KEY = 'ledgers.showDownloadButtons';

export function LedgersIndexPage() {
  const parties = useQuery<Party[]>({
    queryKey: ['parties'],
    queryFn: () => api.get<Party[]>('/api/parties'),
  });
  const nonSelf = useMemo(
    () => (parties.data ?? []).filter((p) => p.role !== 'self'),
    [parties.data],
  );

  const today = useMemo(() => todayIso(), []);

  const balanceQueries = useQueries({
    queries: nonSelf.map((p) => ({
      queryKey: ['balance', p.id],
      queryFn: () => api.get<PartyBalance>(`/api/parties/${p.id}/balance`),
    })),
  });

  const openingQueries = useQueries({
    queries: nonSelf.map((p) => ({
      queryKey: ['opening-balance', p.id, today],
      queryFn: () =>
        api
          .get<OpeningBalance | null>(`/api/opening-balances/${p.id}?asOf=${today}`)
          .catch(() => null),
    })),
  });

  const [filter, setFilter] = useState('');
  const [showDownloads, setShowDownloads] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(SHOW_DL_KEY) === 'true';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(SHOW_DL_KEY, showDownloads ? 'true' : 'false');
  }, [showDownloads]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (q.length === 0) return nonSelf;
    return nonSelf.filter((p) => p.displayName.toLowerCase().includes(q));
  }, [filter, nonSelf]);

  // KPI aggregates
  const kpis = useMemo(() => {
    let net = 0;
    let uplineDebt = 0;
    let clientReceivables = 0;
    nonSelf.forEach((p, i) => {
      const b = balanceQueries[i]?.data?.balance;
      if (!b || b === '…') return;
      const n = parseFloat(b);
      if (!Number.isFinite(n)) return;
      net += n;
      if (p.role === 'upline') uplineDebt += n;
      if (p.role === 'client') clientReceivables += n;
    });
    return { net, uplineDebt, clientReceivables };
  }, [balanceQueries, nonSelf]);

  return (
    <AppShell
      title="Ledgers"
      toolbar={
        <PageToolbar
          title="Ledgers"
          primary={
            <div className="flex items-center gap-2">
              <TextInput
                placeholder="Search parties…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                name="ledger-search"
                className="min-h-[36px] w-[160px] py-1.5 sm:w-[200px]"
              />
              <div className="hidden sm:block">
                <Checkbox
                  label="Show download buttons"
                  checked={showDownloads}
                  onChange={setShowDownloads}
                />
              </div>
            </div>
          }
        />
      }
    >
      {/* KPI summary */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:mb-6 sm:grid-cols-3">
        <KpiCard
          label="Net position"
          value={kpis.net}
          hint="Sum of all party balances"
        />
        <KpiCard
          label="Upline debt"
          value={kpis.uplineDebt}
          hint="What you owe uplines"
          invertTone
        />
        <KpiCard
          label="Client receivables"
          value={kpis.clientReceivables}
          hint="What clients owe you"
        />
      </div>

      {/* Mobile-only extra toggle */}
      <div className="mb-4 sm:hidden">
        <Checkbox
          label="Show download buttons"
          checked={showDownloads}
          onChange={setShowDownloads}
        />
      </div>

      {parties.isLoading ? (
        <Card padding={false}>
          <div className="px-4 py-3">
            <RowSkeleton rows={5} />
          </div>
        </Card>
      ) : nonSelf.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No parties yet"
          description="Add clients and your upline to start tracking balances."
          action={
            <Btn size="sm" onClick={() => window.location.assign('/wizard')}>
              Go to Setup
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </Btn>
          }
        />
      ) : filtered.length === 0 ? (
        <Card>
          <p className="text-small text-ink-muted">No parties match your search.</p>
        </Card>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Card padding={false} className="overflow-x-auto">
              <table className="min-w-full text-small">
                <thead className="bg-surface-raised">
                  <tr>
                    <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      Party
                    </th>
                    <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      Role
                    </th>
                    <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                      Balance (K-INR)
                    </th>
                    {showDownloads && (
                      <th className="px-4 py-2.5 text-left text-caption font-semibold uppercase tracking-wide text-ink-muted">
                        Download
                      </th>
                    )}
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {filtered.map((p) => {
                    const idx = nonSelf.findIndex((np) => np.id === p.id);
                    const balQ = balanceQueries[idx];
                    const bal = balQ?.data?.balance ?? '…';
                    const opening = openingQueries[idx]?.data ?? null;
                    const from = opening
                      ? addDaysIso(opening.asOfDate, 1)
                      : addDaysIso(today, -365);
                    return (
                      <tr key={p.id} className="transition-colors hover:bg-surface-raised/60">
                        <td className="px-4 py-3 font-medium text-ink">
                          {p.displayName}
                        </td>
                        <td className="px-4 py-3">
                          <StatePill state={p.role} />
                        </td>
                        <td className={`px-4 py-3 font-mono font-semibold tabular-nums ${toneFromBalance(bal)}`}>
                          {bal}
                        </td>
                        {showDownloads && (
                          <td className="px-4 py-3">
                            <DownloadLinks
                              partyId={p.id}
                              partyName={p.displayName}
                              from={from}
                              to={today}
                            />
                          </td>
                        )}
                        <td className="px-4 py-3 text-right">
                          <Link
                            to={`/parties/${p.id}/ledger`}
                            className="inline-flex items-center gap-1 text-caption text-brand hover:underline"
                          >
                            Open ledger
                            <ArrowRight className="h-3 w-3" strokeWidth={2} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </div>

          {/* Mobile cards */}
          <div className="grid grid-cols-1 gap-3 md:hidden">
            {filtered.map((p) => {
              const idx = nonSelf.findIndex((np) => np.id === p.id);
              const balQ = balanceQueries[idx];
              const bal = balQ?.data?.balance ?? '…';
              const opening = openingQueries[idx]?.data ?? null;
              const from = opening
                ? addDaysIso(opening.asOfDate, 1)
                : addDaysIso(today, -365);
              return (
                <PartyCard
                  key={p.id}
                  party={p}
                  balance={bal}
                  showDownloads={showDownloads}
                  from={from}
                  to={today}
                />
              );
            })}
          </div>
        </>
      )}
    </AppShell>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  hint,
  invertTone = false,
}: {
  label: string;
  value: number;
  hint: string;
  invertTone?: boolean;
}) {
  const sign = value < 0 ? -1 : value > 0 ? 1 : 0;
  const effective = invertTone ? -sign : sign;
  const colour =
    effective < 0 ? 'text-neg' : effective > 0 ? 'text-pos' : 'text-ink-muted';
  return (
    <Card>
      <div className="text-caption font-medium uppercase tracking-wide text-ink-muted">
        {label}
      </div>
      <div className={`mt-1 font-mono text-display font-bold tabular-nums ${colour}`}>
        {value.toFixed(3)}
      </div>
      <div className="mt-1 text-caption text-ink-muted">{hint}</div>
    </Card>
  );
}

function PartyCard({
  party,
  balance,
  showDownloads,
  from,
  to,
}: {
  party: Party;
  balance: string;
  showDownloads: boolean;
  from: string;
  to: string;
}) {
  const signText =
    balance === '…' || balance === '0'
      ? ''
      : balance.startsWith('-')
      ? 'you owe'
      : 'owes you';
  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-small font-semibold text-ink">
            {party.displayName}
          </div>
          <div className="mt-1">
            <StatePill state={party.role} />
          </div>
        </div>
        <Link
          to={`/parties/${party.id}/ledger`}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-small font-medium text-brand transition-colors hover:bg-surface-raised"
        >
          Open
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
        </Link>
      </div>
      <div className="mt-3">
        <div className={`font-mono text-display font-bold tabular-nums ${toneFromBalance(balance)}`}>
          {balance}
        </div>
        {signText && (
          <div className="mt-0.5 text-caption uppercase tracking-wide text-ink-muted">
            {signText}
          </div>
        )}
      </div>
      {showDownloads && (
        <div className="mt-3 border-t border-border pt-3">
          <DownloadLinks
            partyId={party.id}
            partyName={party.displayName}
            from={from}
            to={to}
          />
        </div>
      )}
    </Card>
  );
}

function DownloadLinks({
  partyId,
  partyName,
  from,
  to,
}: {
  partyId: number;
  partyName: string;
  from: string;
  to: string;
}) {
  const pdfHref = `/api/parties/${partyId}/ledger.pdf?from=${from}&to=${to}`;
  const pngHref = `/api/parties/${partyId}/ledger.png?from=${from}&to=${to}`;
  const slug = slugify(partyName) || `party-${partyId}`;
  return (
    <div className="flex items-center gap-2">
      <a
        href={pdfHref}
        target="_blank"
        rel="noopener"
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-caption font-medium text-ink transition-colors hover:bg-surface-raised"
        title="Open PDF in a new tab"
      >
        <FileText className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} />
        PDF
      </a>
      <a
        href={pngHref}
        download={`ledger-${slug}-${from}-${to}.png`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-caption font-medium text-ink transition-colors hover:bg-surface-raised"
        title="Download PNG"
      >
        <ImageIcon className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} />
        PNG
      </a>
    </div>
  );
}

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toneFromBalance(v: string): string {
  if (v === '…' || v === '0') return 'text-ink-muted';
  if (v.startsWith('-')) return 'text-neg';
  return 'text-pos';
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear().toString().padStart(4, '0');
  const m = (d.getMonth() + 1).toString().padStart(2, '0');
  const day = d.getDate().toString().padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDaysIso(iso: string, delta: number): string {
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10));
  const date = new Date(y!, (m ?? 1) - 1, d ?? 1);
  date.setDate(date.getDate() + delta);
  const yy = date.getFullYear().toString().padStart(4, '0');
  const mm = (date.getMonth() + 1).toString().padStart(2, '0');
  const dd = date.getDate().toString().padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

// BookOpen available for empty-states elsewhere.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _bookopenRef = BookOpen;
